<template>
  <div
    :class="{
      'no-wrap': dense && tiny,
      'q-col-gutter-sm': dense && tiny,
      'q-col-gutter-md': !dense || !tiny
    }"
    class="row"
  >
    <span>
      <interval-timer
        v-if="hasDataObject"
        ref="timer"

        v-model="data.currentInterval"
        :interval-time="sessionProgram.program.interval"
        :duration="data.duration"
        :timer-interval-pause="sessionProgram.program.timerIntervalPause"

        :paused="data.isTimerPaused"
        :duration-offset="timerOffsetTimestamp"

        :session-program="sessionProgram.id"

        :session-paused="sessionPaused"
        :session-duration-offset="sessionDurationOffset"

        :disable="disable"
        :dense="dense"
        :tiny="tiny"
        :warning="intervalWarning"
        :warning-sound="intervalWarningSound"
        :ios-audio="iosAudio"
        :mute-audio="muteAudio"
        class="bg-primary-light full-height"

        @update:model-value="onRecordIntervalTimer"
        @start="onResumeIntervalTimer"
        @pause="onPauseIntervalTimer"
        @update="onUpdateIntervalTimer"
      />
    </span>
    <template
      v-for="target in sortedTargets"
      :key="target.id"
    >
      <target-data
        ref="targets"
        :session-program="sessionProgram"
        :target="target"
        :data="data"
        :active-prompt-levels="activePromptLevels"
        :workflow-statuses="workflowStatuses"
        :dense="dense"
        :tiny="tiny"
        :disable="disable"
        @record="onRecord"
        @record-multiple-data="onRecordMultipleData"
        @record-program-data="onRecordProgramData"
      />
    </template>
  </div>
</template>
<script>

import IntervalTimer from 'components/Programs/DataCollection/IntervalTimer';
import TargetData from 'components/Programs/DataCollection/TargetData';
import { Timestamp } from 'firebase/firestore';
import { chain, get, isEmpty, isObject } from 'lodash';
import { mapState } from 'pinia';
import DataCollectionProgram from 'src/mixins/DataCollectionProgram';
import { sessions } from 'src/store/sessions';

export default {
  name: 'IntervalProgram',

  components: {
    IntervalTimer,
    TargetData,
  },

  mixins: [DataCollectionProgram],

  emits: ['record-program-data'],

  computed: {
    ...mapState(sessions, ['currentVideoCallSession']),

    hasDataObject() {
      return isObject(this.data);
    },

    // Don't play sounds if we are in a video call - it will interfere with the call
    muteAudio() {
      return !!this.currentVideoCallSession;
    },

    timerOffsetTimestamp() {
      const timerOffsetTimestamp = get(this.data, 'timerOffsetTimestamp');
      if (chain(timerOffsetTimestamp).get('toDate').isFunction().value()) {
        return timerOffsetTimestamp.toDate();
      }
      return null;
    }, // timerOffsetTimestamp()

    intervalWarning() {
      if (!get(this.sessionProgram, ['program', 'timerWarning'], false)) {
        return null;
      }
      return get(this.sessionProgram, ['program', 'timerWarningSeconds'], 0);
    }, // intervalWarning()

    intervalWarningSound() {
      if (!get(this.sessionProgram, ['program', 'timerWarning'], false)) {
        return '';
      }
      return get(this.sessionProgram, ['program', 'timerWarningSound'], '');
    }, // intervalWarningSound()

  },

  methods: {

    finishSession() {
      const { interval, timerAutoStart, timerIntervalPause } = this.sessionProgram.program;
      const finalInterval = (timerAutoStart && !timerIntervalPause && interval > 0)
        ? this.getFinalInterval(interval)
        : undefined;

      const finalData = chain(this.$refs)
        .get('targets')
        .map((targetRef) => {
          if (chain(targetRef).get('finishSession').isFunction().value()) {
            return targetRef.finishSession(finalInterval);
          }
          return null;
        })
        .flatten()
        .compact()
        .value();

      if (chain(this.$refs).get(['timer', 'finishSession']).isFunction().value()) {
        this.$refs.timer.finishSession();
      }
      if (!isEmpty(finalData)) {
        return { finalData };
      }
    },

    /**
     * The interval the program timer is in at the moment the session is finished. It is based on the timer's own
     * elapsed time and not on the session duration, so paused time is not counted as passed intervals
     * */
    getFinalInterval(interval) {
      if (!chain(this.$refs).get(['timer', 'getTime']).isFunction().value()) {
        return undefined;
      }
      const currentInterval = get(this.data, 'currentInterval') || 1;
      return currentInterval + Math.floor(this.$refs.timer.getTime() / interval);
    }, // getFinalInterval()

    onRecordIntervalTimer(currentInterval, isTimerPaused = false, timerOffsetTimestamp = Timestamp.now()) {
      if (this.disable) {
        return;
      }

      const dataObj = {
        currentInterval,
        duration: 0,
        timerOffsetTimestamp,
        isTimerPaused,
      };

      this.$emit('record-program-data', this.sessionProgram, dataObj);
    },

    onUpdateIntervalTimer(duration, timerOffsetTimestampDate = null) {
      const timerOffsetTimestamp = (timerOffsetTimestampDate !== null)
        ? Timestamp.fromDate(timerOffsetTimestampDate)
        : null;

      const dataObj = {
        duration,
        timerOffsetTimestamp,
      };

      this.$emit('record-program-data', this.sessionProgram, dataObj);
    }, // onUpdateIntervalTimer()

    onPauseIntervalTimer(duration) {
      if (this.disable) {
        return;
      }

      const dataObj = {
        duration,
        timerOffsetTimestamp: null,
        isTimerPaused: true,
      };

      this.$emit('record-program-data', this.sessionProgram, dataObj);
    },

    onResumeIntervalTimer(duration) {
      const dataObj = {
        duration,
        timerOffsetTimestamp: Timestamp.now(),
        isTimerPaused: false,
      };

      this.$emit('record-program-data', this.sessionProgram, dataObj);
    },

  },
};
</script>
